import { Spinner, SpinnerUntil } from "@edgetier/components";
import { Button, Box, Divider, Stack, Menu, Tooltip } from "@edgetier/global-components";
import { doNothing } from "@edgetier/utilities";
import classNames from "classnames";
import { CSSProperties, Fragment, memo, useCallback, useEffect, useMemo, useRef } from "react";
import { Virtuoso, VirtuosoHandle } from "react-virtuoso";

import { IProps } from "./select-menu.types";
import SelectSelectedList from "./select-selected-list";
import { APPLY_BUTTON_TEXT, CANCEL_BUTTON_TEXT, DEFAULT_ITEM_ICON_COLOUR } from "../select.constants";
import { pluralize } from "@edgetier/utilities";
import { calculateGroupedFlatItemIndex, isEndScroll } from "./select-menu.utilitites";
import { SelectMenuMessage } from "./select-menu-message";
import { SelectInfoMessage } from "./select-info-message";
import styles from "./select-menu.module.scss";
import { SELECT_ALL_ITEMS_LENGTH_LIMIT } from "./select-menu.constants";
import { SelectMenuHeader } from "./select-menu-header";
import { Text } from "../../text";
import { Icon } from "../../icon";
import { Group } from "../../group";
import { Accordion } from "../../accordion";
import { kebabCase } from "lodash";
import { rem } from "@mantine/core";
/**
 * Menu to allow users to choose one or more items from a list.
 * @param props.allItems                        An array of all the items, regardless of whether they are selected/unselected.
 * @param props.addItemMenu                     A menu for adding an item to the select.
 * @param props.addItemButtonProps              Props for the add item button.
 * @param props.children                        Optional method to render items.
 * @param props.close                           Method to close the menu.
 * @param props.description                     Description of items to be selected.
 * @param props.disableMenuItems                Whether the menu items should be disabled or not.
 * @param props.getGroup                        Getter for an item's group name.
 * @param props.getLabel                        Getter for an item's label.
 * @param props.getValue                        Getter for an item's value.
 * @param props.getIcon                         Getter for an item's icon.
 * @param props.getIconProps                    Getter for extra props for an item's icon, e.g. its colour.
 * @param props.isLoading                       Optional loading state of the items.
 * @param props.isSingleSelect                  Whether one or more items can be selected.
 * @param props.message                         An optional message to show in the select menu.
 * @param props.noItemsFoundLabel               Label to show when no items are found.
 * @param props.onSelectItems                   Handler when items are selected.
 * @param props.onMassSelect                    Handler when all items are selected or deselected.
 * @param props.onInputChange                   A function that gets called when the select's input value changes.
 * @param props.highlightedIndex                Index of the highlighted item.
 * @param props.isKeyboardHighlight             Whether the keyboard, rather than the mouse, moved the highlight there.
 * @param props.getItemProps                    Getter for the props of an item.
 * @param props.getSelectedItemProps            Getter for the props of a selected item.
 * @param props.onKeyDownCapture                 Answer keys the menu would otherwise take for itself.
 * @param props.removeSelectedItem              Method to remove a selected item.
 * @param props.setActiveIndex                    Move the keyboard to a given selected item.
 * @param props.selectedItems                   Items selected.
 * @param props.setSelectedItems                Method to set the selected items.
 * @param props.notSelectedItems                Items not selected.
 * @param props.selectedValues                  Values selected before opening the menu.
 * @param props.selectedItemsLimit              Maximum number of items that can be selected.
 * @param props.infoMessage                     An optional configuration for the info message. Contains a callback to determine if the message should be shown and the message itself.
 * @param props.itemsSelectedBeforeOpen         The items that were selected before the select menu was opened.
 * @param props.virtualized                     If the lists should be virtualized.
 * @param props.itemheight                      The list item height, if virtualized.
 * @param props.titleSection                    An optional configuration for the title section. Title section won't be shown if not provided.
 * @param props.titleSection.title              The title being showed in the title section.
 * @param props.titleSection.tooltipContent     The tooltip content for the title section. Tooltip won't be shown if not provided.
 * @param props.hideBulkActionSelectAll         Whether to hide the "select all" bulk action button.
 * @param props.hideBulkActionDeselectAll       Whether to hide the "deselect all" bulk action button.
 * @param props.disableBulkActionSelectAll      Whether to disable the "select all" bulk action button.
 * @param props.disableBulkActionDeselectAll    Whether to disable the "deselect all" bulk action button.
 * @param props.selectAllDisabledTooltip        Tooltip to show when the "select all" button is disabled.
 * @param props.deselectAllDisabledTooltip      Tooltip to show when the "deselect all" button is disabled.
 * @param props.isPaginated                     Whether the list is paginated or not.
 * @param props.isNextPageLoading               Whether the next page is loading or not.
 * @param props.isNextPageError                 Whether the next page has an error or not.
 * @param props.onScrollEndReached              Callback to call when the end of the scroll is reached.
 * @param props.onRetry                         Callback to call when the retry button is clicked.
 * @param props.isSaving                        Whether the menu is in a saving state or not.
 * @param props.hasAsyncAction                  Whether the menu has an async action or not.
 * @param props.hasNextPage                     Whether the menu has a next page or not.
 * @param props.isMultipleSelectCheckbox        Whether the menu is a multiple select with checkboxes or not.
 * @param props.enableAutoFocus                 Whether the menu should be focused when opened.
 * @param props.messageTooltip                  Tooltip to show when the message is shown.
 * @param props.menuListProps                   Props for the menu list.
 * @param props.isCompact                       Whether the menu is compact or not.
 * @param props.isItemDisabled                  Function to determine if an item is disabled or not.
 * @param props.getGroupIcon                    Function to get an icon for a group.
 * @param props.getDisabledItemTooltip          Getter for the tooltip text of a disabled item.
 * @param props.includeFooterMessage            Whether to include a footer message or not.
 * @returns Menu of items to choose.
 */
const SelectMenu = <IItem extends {}, IValue extends {} = string>({
    allItems,
    addItemMenu,
    addItemButtonProps,
    children,
    close,
    description = "item",
    disableMenuItems = false,
    isItemDisabled,
    getGroup,
    getLabel,
    getValue,
    getIcon,
    getIconProps,
    isCompact = false,
    isLoading = false,
    isSingleSelect = false,
    message,
    noItemsFoundLabel,
    onSelectItems,
    onMassSelect = doNothing,
    highlightedIndex,
    isKeyboardHighlight = false,
    getItemProps,
    getSelectedItemProps,
    onKeyDownCapture,
    removeSelectedItem,
    setActiveIndex,
    selectedItems,
    setSelectedItems,
    notSelectedItems,
    selectedValues,
    menuListProps: externalMenuListProps,
    isMultipleSelectCheckbox,
    enableAutoFocus = true,
    messageTooltip,
    selectedItemsLimit,
    infoMessage,
    itemsSelectedBeforeOpen = selectedItems, // Default to selectedItems as these will match most of the time.
    hideBulkActionSelectAll,
    hideBulkActionDeselectAll,
    disableBulkActionSelectAll,
    disableBulkActionDeselectAll,
    selectAllDisabledTooltip,
    deselectAllDisabledTooltip,
    virtualized,
    titleSection,
    isSaving,
    hasAsyncAction,
    hasNextPage,
    isNextPageLoading,
    isNextPageError,
    onScrollEndReached,
    onRetry,
    selectAllItemsLengthLimit,
    isPaginated,
    getGroupIcon,
    getDisabledItemTooltip,
    includeFooterMessage = false,
    ...other
}: IProps<IItem, IValue>) => {
    const isMobileMultiSelect = isMultipleSelectCheckbox && !isSingleSelect;

    const menuListProps = useMemo(() => {
        return {
            ...externalMenuListProps,
            onScroll: (event: React.UIEvent<HTMLUListElement>) => {
                if (
                    !virtualized &&
                    isEndScroll(
                        event.currentTarget.scrollTop,
                        event.currentTarget.clientHeight,
                        event.currentTarget.scrollHeight
                    )
                ) {
                    onScrollEndReached?.();
                }
            },
        };
    }, [externalMenuListProps, onScrollEndReached, virtualized]);

    const { flatItemIndexMap, groupedItemsWithIndex } = useMemo(
        () => calculateGroupedFlatItemIndex(notSelectedItems, getGroup),
        [notSelectedItems, getGroup]
    );

    const itemLimit = useMemo(
        () => (typeof selectedItemsLimit === "number" ? selectedItemsLimit : selectedItemsLimit?.limit),
        [selectedItemsLimit]
    );

    const limitMessage = useMemo(
        () => (typeof selectedItemsLimit === "number" ? `${description} limit reached` : selectedItemsLimit?.message),
        [selectedItemsLimit, description]
    );

    const limitReached = useMemo(
        () => (typeof itemLimit === "undefined" ? false : selectedItems.length >= itemLimit),
        [selectedItems, itemLimit]
    );

    const { flatItemIndexMap: mobileFlatItemIndexMap } = useMemo(() => {
        // Calculate grouped flat items for filtered items
        return calculateGroupedFlatItemIndex(notSelectedItems, getGroup);
    }, [getGroup, notSelectedItems]);

    const withoutMultipleGroupsItems = useMemo(() => {
        return Array.from(isMobileMultiSelect ? mobileFlatItemIndexMap.entries() : flatItemIndexMap.entries());
    }, [isMobileMultiSelect, mobileFlatItemIndexMap, flatItemIndexMap]);

    /**
     * Select some items and close the menu.
     * @param items Newly selected items.
     */
    const selectAndClose = (items: IItem[]) => {
        onSelectItems(items.map(getValue), items);
        !hasAsyncAction && close();
    };

    /**
     * Close the menu and return the selected options.
     */
    const onApply = (currentlySelectedItems: IItem[]) => {
        selectAndClose(currentlySelectedItems);
    };

    /**
     * Select all items.
     * If there is an item limit, select the first n items in the list
     * Filters out disabled items
     */
    const onSelectAll = useCallback(() => {
        // Filter out disabled items
        const selectableItems = notSelectedItems.filter((item) => !isItemDisabled?.(item));

        if (typeof itemLimit !== "undefined") {
            const itemsToSelect = selectableItems.slice(0, itemLimit - selectedItems.length);
            setSelectedItems(selectedItems.concat(itemsToSelect));
        } else {
            setSelectedItems(selectedItems.concat(selectableItems));
        }
        onMassSelect();
    }, [notSelectedItems, onMassSelect, selectedItems, itemLimit, setSelectedItems, isItemDisabled]);

    /**
     * Clear all items.
     */
    const onSelectNone = useCallback(() => {
        setSelectedItems([]);
        onMassSelect();
    }, [onMassSelect, setSelectedItems]);

    const hasMultipleGroups = useMemo(() => Object.keys(groupedItemsWithIndex).length > 1, [groupedItemsWithIndex]);

    const accordionError = useCallback(() => {
        console.error("Please use AutoMenu.WithPlainItems if your select menu has groups and is single select.");
        return null;
    }, []);

    /**
     * Create a list item to be shown in the menu.
     * @param item  Item to be shown.
     * @param index Index of the item.
     */
    const createListItem = useCallback(
        (item: IItem, index: number, style?: CSSProperties) => {
            const value = getValue(item);
            const key = typeof value === "object" ? Object.values(value).join() : value.toString();
            const isSelected = selectedItems.includes(item);
            const isItemDisabledStatus = isItemDisabled?.(item) || disableMenuItems || limitReached;

            const menuItem = (
                <Fragment key={key}>
                    {!hasMultipleGroups &&
                        typeof getGroup === "function" &&
                        (index === 0 || getGroup(item) !== getGroup(notSelectedItems[index - 1])) && (
                            <Menu.Label>{getGroup(item)}</Menu.Label>
                        )}
                    <Menu.Item
                        key={key}
                        aria-label={getLabel(item)}
                        className={classNames(styles["select-menu__option"], {
                            [styles["select-menu__option--is-highlighted"]]: index === highlightedIndex,
                            [styles["select-menu__option--is-disabled"]]: isItemDisabledStatus,
                        })}
                        data-testid={`select-menu-item-${kebabCase(getLabel(item))}`}
                        data-keyboard-highlighted={(isKeyboardHighlight && index === highlightedIndex) || undefined}
                        style={style}
                        {...getItemProps({ item, index })}
                        onMouseDown={() => {}} // This is needed to allow to click inside the modal
                    >
                        <Group w="100%" align="center" wrap="nowrap" gap="sm">
                            {typeof getIcon === "function" && (
                                <Icon icon={getIcon(item)} color={DEFAULT_ITEM_ICON_COLOUR} {...getIconProps?.(item)} />
                            )}
                            <Text size="sm" className={styles["select-menu__option__label"]}>
                                {isMobileMultiSelect ? (
                                    <div className={styles["select-menu__option-checkbox-container"]}>
                                        <input type="checkbox" checked={isSelected} onChange={() => {}} />
                                        {typeof children === "function" ? children(item) : getLabel(item)}
                                    </div>
                                ) : typeof children === "function" ? (
                                    children(item)
                                ) : (
                                    getLabel(item)
                                )}
                            </Text>
                        </Group>
                    </Menu.Item>
                </Fragment>
            );

            // Wrap with tooltip if item is disabled and tooltip is provided
            const tooltipText =
                isItemDisabledStatus && getDisabledItemTooltip ? getDisabledItemTooltip(item) : undefined;
            if (isItemDisabledStatus && tooltipText) {
                return (
                    <Tooltip key={key} label={tooltipText} withArrow>
                        <Box>{menuItem}</Box>
                    </Tooltip>
                );
            }

            return menuItem;
        },
        [
            selectedItems,
            getGroup,
            getLabel,
            getValue,
            hasMultipleGroups,
            isItemDisabled,
            disableMenuItems,
            limitReached,
            highlightedIndex,
            isKeyboardHighlight,
            isMobileMultiSelect,
            children,
            getItemProps,
            notSelectedItems,
            getIcon,
            getIconProps,
            getDisabledItemTooltip,
        ]
    );

    const hasNoItems = useMemo(
        () => notSelectedItems.length === 0 && selectedItems.length === 0,
        [notSelectedItems.length, selectedItems.length]
    );

    const multiSelectHasNoItems = useMemo(
        () => !isSingleSelect && notSelectedItems.length === 0,
        [isSingleSelect, notSelectedItems.length]
    );

    const virtuosoRef = useRef<VirtuosoHandle>(null);

    // Keep the option the keyboard highlights in view. A virtualised list only renders the rows on screen, so Downshift
    // has no row to scroll to and the virtualiser is asked instead. This follows the highlight, not key presses on the
    // page, because a select inside a menu or popover may stop its key presses from going any further.
    useEffect(() => {
        if (virtualized && isKeyboardHighlight && highlightedIndex >= 0) {
            virtuosoRef.current?.scrollToIndex({ index: highlightedIndex, align: "start", behavior: "smooth" });
        }
    }, [highlightedIndex, isKeyboardHighlight, virtualized]);

    const isSelectAllBulkActionDisabled = useMemo(
        () =>
            disableBulkActionSelectAll ||
            (typeof disableBulkActionSelectAll === "undefined" &&
                notSelectedItems.length + selectedItems.length >
                    (selectAllItemsLengthLimit ?? SELECT_ALL_ITEMS_LENGTH_LIMIT)),
        [disableBulkActionSelectAll, notSelectedItems, selectedItems, selectAllItemsLengthLimit]
    );

    const selectAllBulkActionTooltipText = useMemo(
        () =>
            selectAllDisabledTooltip || notSelectedItems.length === 0
                ? ""
                : `To improve performance, selecting all is not available when list exceeds ${selectAllItemsLengthLimit ?? SELECT_ALL_ITEMS_LENGTH_LIMIT} items.`,
        [selectAllDisabledTooltip, notSelectedItems.length, selectAllItemsLengthLimit]
    );

    const isSelectAllDisabled = useMemo(() => {
        return notSelectedItems.length === 0 || disableMenuItems || limitReached || isSelectAllBulkActionDisabled;
    }, [notSelectedItems.length, disableMenuItems, limitReached, isSelectAllBulkActionDisabled]);

    const isDeselectAllDisabled = useMemo(() => {
        return selectedItems.length === 0 || disableMenuItems || disableBulkActionDeselectAll;
    }, [selectedItems.length, disableMenuItems, disableBulkActionDeselectAll]);

    const totalItems = useMemo(
        () => notSelectedItems.length + selectedItems.length,
        [notSelectedItems.length, selectedItems.length]
    );

    return (
        <Box
            onKeyDownCapture={onKeyDownCapture}
            className={classNames(styles["select-menu"], {
                [styles["select-menu--is-multiple-select"]]: !isSingleSelect && !isMultipleSelectCheckbox,
                [styles["select-menu--is-single-select"]]: isSingleSelect,
            })}
            data-testid={
                !isSingleSelect && !isMultipleSelectCheckbox
                    ? "select-menu__multiple-select__container"
                    : "select-menu__container"
            }
        >
            {!isSingleSelect && (
                <SelectMenuHeader
                    notSelectedItems={notSelectedItems}
                    deselectAllDisabledTooltip="Deselect all is not available when list exceeds 1000 items."
                    hideBulkActionDeselectAll={hideBulkActionDeselectAll ?? false}
                    isDeselectAllDisabled={isDeselectAllDisabled ?? false}
                    onSelectNone={onSelectNone}
                    itemLimit={itemLimit}
                    selectedItems={selectedItems}
                    hideBulkActionSelectAll={hideBulkActionSelectAll ?? false}
                    selectAllBulkActionTooltipText={selectAllBulkActionTooltipText}
                    isSelectAllDisabled={isSelectAllDisabled}
                    onSelectAll={onSelectAll}
                />
            )}
            <Stack gap={0} className={styles["container"]}>
                {typeof infoMessage !== "undefined" &&
                    (typeof infoMessage.showMessage === "undefined" || infoMessage.showMessage(selectedItems)) && (
                        <Box px="sm" py="md">
                            <SelectInfoMessage message={infoMessage.message} />
                        </Box>
                    )}
                {titleSection && <Menu.Info title={titleSection?.title} subtitle={titleSection?.infoMessage ?? ""} />}
                <div
                    className={classNames(styles["select-menu__lists"], {
                        [styles["select-menu__virtualized-lists"]]: virtualized,
                    })}
                >
                    <div
                        className={classNames(styles["select-menu__list"], styles["select-menu__list--not-selected"])}
                        data-testid="select-menu__list"
                    >
                        <div className={styles["select-menu__options"]} data-testid="select-menu__options">
                            {/** Use itemsSelectedBeforeOpen here because that will be more up-to-date than selectedItems */}
                            {!isCompact && isSingleSelect && itemsSelectedBeforeOpen.length > 0 && (
                                <Group
                                    className={styles["select-menu__selected-item"]}
                                    gap="sm"
                                    align="center"
                                    data-testid="select-menu__selected-item"
                                >
                                    {typeof getIcon === "function" && (
                                        <Icon
                                            icon={getIcon(itemsSelectedBeforeOpen[0])}
                                            color={DEFAULT_ITEM_ICON_COLOUR}
                                            {...getIconProps?.(itemsSelectedBeforeOpen[0])}
                                        />
                                    )}

                                    <Text span size="sm" flex={1} truncate>
                                        {typeof children === "function"
                                            ? children(itemsSelectedBeforeOpen[0])
                                            : getLabel(itemsSelectedBeforeOpen[0])}
                                    </Text>
                                </Group>
                            )}

                            {!isLoading && (hasNoItems || multiSelectHasNoItems) && (
                                <Text size="sm" dimmed className={styles["empty"]}>
                                    {typeof noItemsFoundLabel === "undefined"
                                        ? `No ${pluralize(description, 0)} found`
                                        : noItemsFoundLabel}
                                </Text>
                            )}

                            <SpinnerUntil data={[]} isReady={!isLoading}>
                                {!hasMultipleGroups ? (
                                    virtualized ? (
                                        <ul
                                            className={styles["select-menu__list"]}
                                            {...menuListProps}
                                            data-testid={!isSingleSelect ? "non-selected-items" : "select-menu__list"}
                                        >
                                            <Virtuoso
                                                ref={virtuosoRef}
                                                className={styles["select-menu__list-virtuoso"]}
                                                style={{ height: "100%" }}
                                                data={withoutMultipleGroupsItems}
                                                endReached={onScrollEndReached}
                                                itemContent={(_, [item, index]) => createListItem(item, index)}
                                                components={{
                                                    Footer: () => (
                                                        <>
                                                            {(isNextPageLoading || isLoading) && <Spinner />}
                                                            {isNextPageError && (
                                                                <div className={styles["select-menu__retry-button"]}>
                                                                    <Button onClick={onRetry}>Retry</Button>
                                                                </div>
                                                            )}
                                                        </>
                                                    ),
                                                }}
                                            />
                                        </ul>
                                    ) : (
                                        <ul
                                            className={styles["select-menu__list"]}
                                            {...menuListProps}
                                            data-testid={!isSingleSelect ? "non-selected-items" : "select-menu__list"}
                                        >
                                            {withoutMultipleGroupsItems.map(([item, index]) =>
                                                createListItem(item, index)
                                            )}
                                            {hasNextPage && (
                                                <div className={styles["select-menu__load-more"]}>
                                                    {(isNextPageLoading || isLoading) && <Spinner />}
                                                    {isNextPageError && (
                                                        <div className={styles["select-menu__retry-button"]}>
                                                            <Button onClick={onRetry}>Retry</Button>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </ul>
                                    )
                                ) : (
                                    <ul
                                        className={styles["select-menu__list"]}
                                        {...menuListProps}
                                        data-testid={!isSingleSelect ? "non-selected-items" : "select-menu__list"}
                                    >
                                        {isSingleSelect && hasMultipleGroups ? (
                                            accordionError()
                                        ) : (
                                            <Accordion>
                                                {Object.entries(groupedItemsWithIndex).map(([group, items]) => (
                                                    <Accordion.Item key={group} value={group} bd="none">
                                                        <Accordion.Control
                                                            className={styles["select-menu__accordion__control"]}
                                                            icon={getGroupIcon?.(group) ?? undefined}
                                                            data-testid={`non-selected-items-group-${kebabCase(group)}`}
                                                        >
                                                            <Text
                                                                span
                                                                size="sm"
                                                                data-testid={`non-selected-items-group-label-${kebabCase(group)}`}
                                                            >
                                                                {group}
                                                            </Text>
                                                        </Accordion.Control>
                                                        <Accordion.Panel pl="xxl">
                                                            {items.map(({ item }, index) => {
                                                                const flatItemIndex = flatItemIndexMap.get(item);
                                                                return createListItem(item, flatItemIndex ?? index);
                                                            })}
                                                            {hasNextPage && (
                                                                <div className={styles["select-menu__load-more"]}>
                                                                    {(isNextPageLoading || isLoading) && <Spinner />}
                                                                    {isNextPageError && (
                                                                        <div
                                                                            className={
                                                                                styles["select-menu__retry-button"]
                                                                            }
                                                                        >
                                                                            <Button onClick={onRetry}>Retry</Button>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            )}
                                                        </Accordion.Panel>
                                                    </Accordion.Item>
                                                ))}
                                            </Accordion>
                                        )}
                                    </ul>
                                )}
                            </SpinnerUntil>
                        </div>
                        {typeof message !== "undefined" && isSingleSelect && (
                            <SelectMenuMessage
                                containerProps={{ p: "md" }}
                                className={styles["single_menu_message"]}
                                message={message}
                                messageTooltip={messageTooltip}
                            />
                        )}
                    </div>
                    <Divider orientation="vertical" color="gray.3" />

                    {!isSingleSelect && !isMultipleSelectCheckbox && (
                        <SelectSelectedList
                            getGroup={getGroup}
                            getLabel={getLabel}
                            getSelectedItemProps={getSelectedItemProps}
                            getValue={getValue}
                            isLoading={isLoading}
                            removeSelectedItem={removeSelectedItem}
                            setActiveIndex={setActiveIndex}
                            selectedItems={selectedItems}
                            itemLimit={itemLimit}
                            limitMessage={limitMessage}
                            virtualized={virtualized}
                            getGroupIcon={getGroupIcon}
                            getIcon={getIcon}
                            getIconProps={getIconProps}
                        >
                            {children}
                        </SelectSelectedList>
                    )}
                </div>
                {!isSingleSelect && (
                    <Group justify="space-between" className={styles["select-menu__controls"]}>
                        {typeof message !== "undefined" && (
                            <SelectMenuMessage message={message} messageTooltip={messageTooltip} />
                        )}
                        <Group ml="auto">
                            {!isMultipleSelectCheckbox && (
                                <Button
                                    onClick={() => close()}
                                    variant="default"
                                    data-testid="cancel-button"
                                    data-select-action
                                >
                                    {CANCEL_BUTTON_TEXT}
                                </Button>
                            )}
                            <Button
                                disabled={
                                    isSaving ||
                                    isLoading ||
                                    (typeof itemLimit !== "undefined" && selectedItems.length > itemLimit)
                                }
                                loading={isSaving}
                                onClick={() => onApply(selectedItems)}
                                variant={isMultipleSelectCheckbox ? "default" : "filled"}
                                className={
                                    isMultipleSelectCheckbox
                                        ? styles["select-menu__controls-mobile-apply"]
                                        : styles["select-menu__controls-apply"]
                                }
                                data-testid="done-button"
                                data-select-action
                            >
                                {APPLY_BUTTON_TEXT}
                            </Button>
                        </Group>
                    </Group>
                )}
                {includeFooterMessage && (
                    <>
                        <Divider />
                        <Stack p={rem(6)} mih={rem(52)} justify="center">
                            <Box py="sm" px="md">
                                <Text size="sm" dimmed>
                                    {totalItems} {pluralize(description, totalItems)} available
                                </Text>
                            </Box>
                        </Stack>
                    </>
                )}
            </Stack>
        </Box>
    );
};

export default memo(SelectMenu) as typeof SelectMenu;
